import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

export {}; // module safety

const API_BASE = "/api";

const getStaffAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("staff_google_token") || "";
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};



type MerchantRow = {
  merchant_id: string;
  company_name: string;

  industry?: string; // 業種
  merchant_type?: string; // 区分（法人 / 個人）

  status: string; // 進行中/完了/承認済 など（バックエンドの値 or 画面用）
  approval_status?: "未承認" | "承認済";

  updated_at: string;

  // ✅ progress page uses sales_persons[].
  // approved list screenshot shows single display too, but we'll support both.
  sales_persons?: string[];
  sales_person_display?: string;
};

/** ---------- SAME STYLES AS StaffProgressList.tsx ---------- **/
const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const filterWrap: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 12,
  background: "#fff",
  marginBottom: 12,
};

const filterGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 0.8fr",
  gap: 10,
  alignItems: "end",
};

const filterLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: 6,
};

const btnSecondary: React.CSSProperties = {
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  cursor: "pointer",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 900,
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: 12,
  fontSize: 13,
  fontWeight: 900,
  color: "#0f172a",
  borderBottom: "1px solid #e2e8f0",
};

const thRight: React.CSSProperties = {
  ...th,
  textAlign: "right",
};

const td: React.CSSProperties = {
  padding: 12,
  verticalAlign: "top",
  color: "#0f172a",
  fontWeight: 800,
  fontSize: 13,
};

const tdRight: React.CSSProperties = {
  ...td,
  textAlign: "right",
};
/** --------------------------------------------------------- **/

export default function StaffApprovedList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState<MerchantRow[]>([]);

  // ✅ Filters (same as progress page)
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"全て" | "進行中" | "完了" | "承認済">("全て");
  const [salesFilter, setSalesFilter] = useState<string>("全て");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`${API_BASE}/staff/progress`, {
  headers: getStaffAuthHeaders(),
});

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          setError(json?.detail || "取得に失敗しました。");
          setLoading(false);
          return;
        }

        setRows(Array.isArray(json?.merchants) ? json.merchants : []);
        setLoading(false);
      } catch {
        setError("バックエンドが起動しているか確認してください。");
        setLoading(false);
      }
    };

    run();
  }, []);

  // ✅ only approved base list
  const approvedBase = useMemo(() => {
    return rows.filter((r) => (r.approval_status || "未承認") === "承認済");
  }, [rows]);

  // ✅ sales dropdown candidates (EXACT SAME idea as StaffProgressList)
  const salesCandidates = useMemo(() => {
    const set = new Set<string>();

    approvedBase.forEach((r) => {
      // prefer array
      (r.sales_persons || []).forEach((n) => {
        const name = (n || "").trim();
        if (name) set.add(name);
      });

      // also support single display
      const disp = (r.sales_person_display || "").trim();
      if (disp) {
        disp
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
          .forEach((x) => set.add(x));
      }
    });

    return ["全て", ...Array.from(set).sort((a, b) => a.localeCompare(b, "ja"))];
  }, [approvedBase]);

  // ✅ Approved page still has status dropdown like progress page
  // but base list is always 承認済, so statusFilter mostly stays 承認済 / 全て.
  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();

    return approvedBase.filter((m) => {
      const displayStatus: "承認済" = "承認済";

      // Search by company name or merchant id
      const searchable = `${m.company_name || ""} ${m.merchant_id || ""}`.toLowerCase();
      const okQuery = !qq || searchable.includes(qq);

      // Status filter
      const okStatus = statusFilter === "全て" ? true : displayStatus === statusFilter;

      // Sales filter
      const persons = (m.sales_persons || [])
        .map((x) => (x || "").trim())
        .filter(Boolean);

      const okSales =
        salesFilter === "全て"
          ? true
          : persons.includes(salesFilter) ||
            (m.sales_person_display || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
              .includes(salesFilter);

      return okQuery && okStatus && okSales;
    });
  }, [approvedBase, q, statusFilter, salesFilter, ]);

  const resetFilters = () => {
    setQ("");
    setStatusFilter("全て");
    setSalesFilter("全て");
  };

  return (
    <StaffLayout title="承認済み一覧" active="approvedMerchants">
      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 900, marginBottom: 12 }}>承認済み一覧</div>

          {/* ✅ Filters (SAME UI AS StaffProgressList) */}
          <div style={filterWrap}>
            <div style={filterGrid}>
              <div>
                <div style={filterLabel}>検索（会社名 / ID）</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="例：Clinic / M01234567"
                  style={inputStyle}
                />
              </div>

              <div>
                <div style={filterLabel}>ステータス</div>
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as "全て" | "進行中" | "完了" | "承認済")
                  }
                  style={inputStyle}
                >
                  <option value="全て">全て</option>
                  <option value="進行中">進行中</option>
                  <option value="完了">完了</option>
                  <option value="承認済">承認済</option>
                </select>
              </div>

              <div>
                <div style={filterLabel}>営業担当者</div>
                <select
                  value={salesFilter}
                  onChange={(e) => setSalesFilter(e.target.value)}
                  style={inputStyle}
                  title={
                    salesCandidates.length <= 1
                      ? "※ 現在、一覧APIに営業担当者が含まれていないため候補が出ません"
                      : "営業担当者で絞り込み"
                  }
                >
                  {salesCandidates.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={filterLabel}>　</div>
                <button type="button" style={btnSecondary} onClick={resetFilters}>
                  クリア
                </button>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              表示件数：{filteredRows.length} 件
            </div>
          </div>

          {/* ✅ Table (same lettering style) */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
            <div
              style={{
                padding: 12,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                fontWeight: 900,
                color: "#0f172a",
              }}
            >
              承認済み：{filteredRows.length} 件
            </div>

            {filteredRows.length === 0 ? (
              <div style={{ padding: 14, color: "#475569", fontWeight: 800 }}>
                条件に一致する承認済み加盟店がありません。
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#ffffff" }}>
                    <th style={th}>会社名</th>
                    <th style={th}>業種</th>
                    <th style={th}>区分</th>
                    <th style={th}>営業担当者</th>
                    <th style={th}>更新日</th>
                    <th style={thRight}>詳細</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRows.map((r) => {
                    const salesDisplay =
                      (r.sales_persons || []).filter(Boolean).join(", ") ||
                      r.sales_person_display ||
                      "-";

                    return (
                      <tr key={r.merchant_id} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td style={td}>
                          <div style={{ fontWeight: 900, color: "#0f172a" }}>
                            {r.company_name || "-"}
                          </div>
                          <div style={{ fontWeight: 800, color: "#475569" }}>
                            ID: {r.merchant_id}
                          </div>
                        </td>

                        <td style={td}>{r.industry || "-"}</td>
                        <td style={td}>{r.merchant_type || "-"}</td>
                        <td style={td}>{salesDisplay}</td>
                        <td style={td}>{r.updated_at || "-"}</td>

                        <td style={tdRight}>
                          <button
                            onClick={() => navigate(`/staff/approved/${r.merchant_id}`)}
                            style={{
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #16a34a",
  background: "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
}}

                          >
                            開く
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
